import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, MenuItem, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { FieldWrapper, PageHeader, SectionHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setAddressData } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { useLazyGetAddressTypeQuery } from "@api/catalogs";
import { usePostValidateNextStageMutation } from "@api/applications";
import { Address } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { ADDRESS_CODES, STATUS_CATALOG } from "@helpers/constantes";
import { ZONA_COBERTURA_WUWEI, ZonaCobertura } from "@helpers/zonasCobertura";
import { estaEnZonaCobertura, getConfigName } from "@helpers/funciones";
import { CODIGO_POSTAL } from "@utils/expreciones";
import useStatusConfig from "@hooks/useStatusConfig";
import { useExistingPersonId } from "@hooks/useExistingPersonId";

interface Colonia {
  id: number;
  name: string;
}

const validationSchema = yup.object().shape({
  cp: yup.string().required("Campo requerido").matches(CODIGO_POSTAL, "Código Postal inválido"),
  calle: yup.string().required("Campo requerido"),
  num_ext: yup.string().required("Campo requerido"),
  colony_id: yup.string().required("Campo requerido"),
  otro: yup.string().when("colony_id", {
    is: "-1",
    then: (schema) => schema.required("Campo requerido"),
  }),
  state_name: yup.string().required("Campo requerido"),
  locality_id: yup.string().required("Campo requerido"),
});

/**
 * Domicilio particular del titular (status CSDOE, ruta `domicilio_pf`), por CP como `DomicilioAccionista`; para
 * cualquier empresa (`WW_Domicilio` en el diseño de WuWei).
 */
/**
 * Empresas que solo operan en una zona (reglas del prototipo). Fuera de ella el domicilio queda guardado y la
 * solicitud pasa a sin cobertura (CSSCO); las que no están aquí aceptan cualquier domicilio.
 */
const ZONA_COBERTURA_POR_EMPRESA: Record<string, ZonaCobertura> = {
  wuwei: ZONA_COBERTURA_WUWEI,
};

export const DomicilioParticular = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.domicilio_pfae.code);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const addressData = useSelector((state: RootState) => state.register.address_data);

  const [getCp] = useLazyGetByCpQuery();
  const [getAddressTypes] = useLazyGetAddressTypeQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const resolvePersonId = useExistingPersonId();

  const [addressTypeId, setAddressTypeId] = useState("");
  const [listColonias, setListColonias] = useState<Colonia[]>([]);
  const [cpError, setCpError] = useState("");
  const [isFetchingCp, setIsFetchingCp] = useState(false);

  const formik = useFormik({
    initialValues: {
      cp: "",
      calle: "",
      num_ext: "",
      num_int: "",
      colony_id: "",
      otro: "",
      state_id: "",
      state_name: "",
      city_id: "",
      city_name: "",
      locality_id: "",
      locality_name: "",
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        setSubmitting(true);

        const dataAddress = {
          street: values.calle,
          no_ext: values.num_ext,
          no_int: values.num_int,
          colonia_id: Number(values.colony_id) === -1 ? null : +values.colony_id,
          state_id: values.state_id ? +values.state_id : null,
          city_id: values.city_id ? +values.city_id : null,
          locality_id: values.locality_id ? +values.locality_id : null,
          country_id: 1,
          cp: values.cp,
          client_details_id: clientDetails.id,
          // La persona del titular, que "Tus datos" dio de alta en la solicitud.
          person_id: await resolvePersonId(application.id),
          address_type_id: addressTypeId,
          other_neighborhood: values.otro,
        };
        const responseAddress = addressData?.id
          ? await patchAddress({ addressId: addressData.id, data: dataAddress }).unwrap()
          : await postAddress(dataAddress).unwrap();

        dispatch(
          setAddressData({
            id: responseAddress.data.id,
            street: values.calle,
            no_ext: values.num_ext,
            no_int: values.num_int,
            colony_id: +values.colony_id,
            state_id: +values.state_id,
            city_id: +values.city_id,
            locality_id: +values.locality_id,
            postal_code: values.cp,
            state_name: values.state_name,
            city_name: values.city_name,
            locality_name: values.locality_name,
            other_neighborhood: values.otro,
          }),
        );

        // Zona de cobertura de la empresa: fuera de ella, sin cobertura (fuera de la secuencia; no consulta buró).
        const zona = ZONA_COBERTURA_POR_EMPRESA[getConfigName(window.location.href)];
        const fueraDeZona =
          zona && !estaEnZonaCobertura({ estado: values.state_name, municipio: values.locality_name }, zona);
        const destino = fueraDeZona ? STATUS_CATALOG.sin_cobertura : nextStatus;
        if (!destino) {
          throw new Error("No se encontró el siguiente status");
        }
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: destino.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();
        navigate(destino.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: { component: "DomicilioParticular-submit", values: formik.values },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue } = formik;

  const handleCp = useCallback(
    async (value: string) => {
      try {
        setIsFetchingCp(true);
        const { data } = await getCp(value);
        const state = data?.states[0];
        const city = data?.cities[0];
        const locality = data?.localities[0];
        const colonia = data?.colonias[0];

        if (!state || !colonia || !locality) {
          ["state_id", "state_name", "city_id", "city_name", "locality_id", "locality_name", "colony_id"].forEach(
            (field) => setFieldValue(field, ""),
          );
          setCpError("No existe el código postal");
          setListColonias([]);
          return;
        }

        setCpError("");
        setFieldValue("state_id", state.id);
        setFieldValue("state_name", state.name);
        setFieldValue("city_id", city ? city.id : "");
        setFieldValue("city_name", city ? city.name : "");
        setFieldValue("locality_id", locality.id);
        setFieldValue("locality_name", locality.name);
        setFieldValue("colony_id", colonia.id);
        setListColonias(data.colonias);
      } catch (error) {
        console.error("Ha ocurrido un error al consultar el código postal", error);
      } finally {
        setIsFetchingCp(false);
      }
    },
    [getCp, setFieldValue],
  );

  // Tipo de domicilio particular (`CDPA`): se consulta al entrar; sin él no se puede guardar.
  useEffect(() => {
    getAddressTypes()
      .unwrap()
      .then((types) =>
        setAddressTypeId(types.find((address: Address) => address.code === ADDRESS_CODES.particular.code)?.id ?? ""),
      )
      .catch((error) => {
        Sentry.captureException(error, { extra: { component: "DomicilioParticular-address_types" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      });
  }, [getAddressTypes, dispatch]);

  // Al volver a la pantalla, el domicilio guardado: el CP vuelve a cargar las colonias.
  useEffect(() => {
    if (!addressData?.id) return;
    const prefill = async () => {
      await handleCp(addressData.postal_code);
      setFieldValue("cp", addressData.postal_code);
      setFieldValue("calle", addressData.street);
      setFieldValue("num_ext", addressData.no_ext);
      setFieldValue("num_int", addressData.no_int);
      setFieldValue("colony_id", String(addressData.colony_id));
      setFieldValue("otro", addressData.other_neighborhood ?? "");
    };
    prefill();
    // Solo al entrar con el domicilio del store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData?.id]);

  const errorOf = (field: keyof typeof formik.values) => (formik.touched[field] && formik.errors[field]) || "";

  return (
    <Box>
      <PageHeader
        eyebrow="Datos y domicilio · Domicilio"
        title="Domicilio"
        subtitle="Ingresa tu domicilio particular. Empieza por tu código postal para autocompletar."
      />

      <Paper variant="lendia_paper">
        <SectionHeader label="Dirección" first />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper label="Código Postal" loading={isFetchingCp} error={cpError || errorOf("cp")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="cp"
              value={formik.values.cp}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                const cp = e.target.value.replace(/\D/g, "");
                setFieldValue("cp", cp);
                if (cp.length === 5) handleCp(cp);
              }}
              placeholder="00000"
              inputProps={{ maxLength: 5, inputMode: "numeric", "data-testid": "domicilio-particular-cp-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper label="Calle o avenida" full error={errorOf("calle")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="calle"
              value={formik.values.calle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Av. Insurgentes Sur"
              inputProps={{ maxLength: 100, "data-testid": "domicilio-particular-calle-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper label="No. Exterior" error={errorOf("num_ext")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_ext"
              value={formik.values.num_ext}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 42"
              inputProps={{ maxLength: 50, "data-testid": "domicilio-particular-num-ext-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper label="No. Interior" optionalSuffix="(opcional)">
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_int"
              value={formik.values.num_int}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 3B"
              inputProps={{ maxLength: 50, "data-testid": "domicilio-particular-num-int-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper label="Colonia" full error={errorOf("colony_id")}>
            {listColonias.length > 0 ? (
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                name="colony_id"
                value={formik.values.colony_id}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (+e.target.value === -1) setFieldValue("otro", "");
                }}
                inputProps={{ "data-testid": "domicilio-particular-colonia-select" }}
                disabled={formik.isSubmitting}
              >
                <MenuItem value="">Selecciona…</MenuItem>
                {listColonias.map((colonia) => (
                  <MenuItem key={colonia.id} value={colonia.id}>
                    {colonia.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                disabled
                placeholder="Se llenará con el CP"
                inputProps={{ "data-testid": "domicilio-particular-colonia-input" }}
              />
            )}
          </FieldWrapper>
          {String(formik.values.colony_id) === "-1" && (
            <FieldWrapper label="Otra colonia" full error={errorOf("otro")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="otro"
                value={formik.values.otro}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 100, "data-testid": "domicilio-particular-otra-colonia-input" }}
                disabled={formik.isSubmitting}
              />
            </FieldWrapper>
          )}
          <FieldWrapper label="Ciudad">
            <TextField
              size="lendia_input"
              fullWidth
              value={formik.values.city_name}
              placeholder="Se llenará con el CP"
              disabled
              inputProps={{ "data-testid": "domicilio-particular-ciudad-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Municipio / Alcaldía">
            <TextField
              size="lendia_input"
              fullWidth
              value={formik.values.locality_name}
              placeholder="Se llenará con el CP"
              disabled
              inputProps={{ "data-testid": "domicilio-particular-municipio-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Entidad federativa" full>
            <TextField
              size="lendia_input"
              fullWidth
              value={formik.values.state_name}
              placeholder="Se llenará con el CP"
              disabled
              inputProps={{ "data-testid": "domicilio-particular-estado-input" }}
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        onBack={() => {
          if (previousStatus) navigate(previousStatus.url);
        }}
        onNext={formik.submitForm}
        backLabel="Atrás"
        nextDisabled={!formik.isValid || formik.isSubmitting || !addressTypeId}
        backTestId="domicilio-particular-back-button"
        nextTestId="domicilio-particular-continue-button"
      />
    </Box>
  );
};
